import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Hero media slot.
 *
 * Renders exactly the same box as the hero <img>. When the CMS hero media type
 * is "video" and a video URL exists, an autoplaying, muted, looping video is
 * layered in after hydration; the hero image stays underneath as the permanent
 * poster/fallback (no video, reduced-motion preference, unsupported browser,
 * blocked autoplay, or decode error).
 *
 * Layout, spacing and typography are untouched — the video uses the exact same
 * className as the image, so the box never shifts.
 */
export function HeroMedia({
  imageUrl,
  imageAlt,
  onImageError,
  mediaType,
  videoUrl,
  videoMobileUrl,
  posterUrl,
  className,
}: {
  imageUrl: string;
  imageAlt: string;
  onImageError: React.ReactEventHandler<HTMLImageElement>;
  mediaType: "image" | "video";
  videoUrl: string;
  videoMobileUrl: string;
  posterUrl: string;
  className: string;
}) {
  // Client-only: keeps SSR output identical to the image-only hero and avoids
  // downloading the video before hydration (no render-blocking, no CLS).
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [failed, setFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    setMounted(true);
    const size = window.matchMedia("(max-width: 767px)");
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncSize = () => setIsMobile(size.matches);
    const syncMotion = () => setReducedMotion(motion.matches);
    syncSize();
    syncMotion();
    size.addEventListener("change", syncSize);
    motion.addEventListener("change", syncMotion);
    return () => {
      size.removeEventListener("change", syncSize);
      motion.removeEventListener("change", syncMotion);
    };
  }, []);

  // One hero video for every screen size: the separate mobile video showed a
  // different clip on phones. Fall back to it only when no main video is set.
  void isMobile;
  const src = videoUrl || videoMobileUrl || "";

  // Browser capability check: if the container/codec is definitely unplayable
  // (e.g. WebM on older Safari), skip the video entirely and keep the poster/
  // image — no wasted bytes, no decode error flash.
  const canPlay = (() => {
    if (!mounted || !src) return false;
    const ext = src.split("?")[0].split(".").pop()?.toLowerCase();
    const type =
      ext === "webm"
        ? 'video/webm; codecs="vp9,opus"'
        : ext === "mp4" || ext === "m4v"
          ? 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"'
          : "";
    if (!type) return true; // unknown extension: let the element try
    const probe = document.createElement("video");
    return probe.canPlayType(type) !== "";
  })();

  const showVideo =
    mounted &&
    !failed &&
    !reducedMotion &&
    canPlay &&
    mediaType === "video" &&
    src.length > 0;
  // Video mode renders ONLY the video: the hero image (including the built-in
  // editorial default) is never used as an underlay, pre-load visual, or poster.
  const videoOnly = mediaType === "video" && src.length > 0;
  const poster = videoOnly ? posterUrl : posterUrl || imageUrl;
  const [isMuted, setIsMuted] = useState(true);

  const toggleMute = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsMuted((prev) => {
      const next = !prev;
      if (videoRef.current) {
        videoRef.current.muted = next;
      }
      return next;
    });
  };

  // A new source (desktop <-> mobile switch, CMS change) gets a clean slate:
  // a decode failure on one file must not disable a different one.
  useEffect(() => {
    setFailed(false);
  }, [src, mediaType]);

  // Autoplay attempt. A rejected promise is NOT a fallback trigger: Chrome
  // rejects with AbortError whenever a load/HMR update interrupts the request,
  // and NotAllowedError only means "keep showing the poster". Retrying on
  // canplay covers both. Only a real decode/network media error (onError)
  // falls back to the hero image.
  useEffect(() => {
    if (!showVideo) return;
    const el = videoRef.current;
    if (!el) return;
    let cancelled = false;
    const tryPlay = () => {
      const attempt = el.play();
      if (attempt && typeof attempt.catch === "function") {
        attempt.catch((e: unknown) => {
          if (cancelled) return;
          const name = (e as { name?: string } | null)?.name;
          if (name === "AbortError") return; // interrupted load, canplay retries
        });
      }
    };
    tryPlay();
    el.addEventListener("canplay", tryPlay);
    el.addEventListener("loadeddata", tryPlay);
    return () => {
      cancelled = true;
      el.removeEventListener("canplay", tryPlay);
      el.removeEventListener("loadeddata", tryPlay);
    };
  }, [showVideo, src]);

  return (
    <>
      {videoOnly ? null : (
        <img
          src={imageUrl}
          alt={imageAlt}
          width={1280}
          height={1600}
          fetchPriority="high"
          loading="eager"
          decoding="async"
          onError={onImageError}
          className={className}
        />
      )}
      {videoOnly && !showVideo ? (
        // Reserves the exact hero box (same aspect classes) while the video
        // mounts — empty, no image, no placeholder visual.
        <div aria-hidden className={className} />
      ) : null}
      {showVideo ? (
        <>
          <video
            key={src}
            ref={videoRef}
            src={src}
            poster={poster}
            autoPlay
            muted={isMuted}
            loop
            playsInline
            preload="metadata"
            controls={false}
            disablePictureInPicture
            controlsList="nodownload noplaybackrate noremoteplayback"
            aria-label={imageAlt}
            role="img"
            tabIndex={-1}
            onError={(e) => {
              const el = e.currentTarget;
              console.error("[hero-video] media error", {
                code: el.error?.code,
                message: el.error?.message,
                src: el.currentSrc,
              });
              setFailed(true);
            }}
            className={videoOnly ? className : `absolute inset-0 ${className}`}
          />
          <button
            type="button"
            onClick={toggleMute}
            aria-label={isMuted ? "Unmute video" : "Mute video"}
            className="absolute bottom-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </>
      ) : null}
    </>
  );
}
